/** @format */

import React, { useEffect, useState } from 'react';
import { Col, Select } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { SHERPA_OPTIONS } from '../types';
import BackOfficeService, { AorData, NpnData } from '../services/BackOfficeService';

interface BackOfficeInfoProps {
  aorId: string;
  npnId: string;
  sherpa: string;
  planTypes: string[];
  isAcaRequest: boolean;
  onAorChange: (value: string) => void;
  onNpnChange: (value: string) => void;
  onSherpaChange: (value: string) => void;
}

export const BackOfficeInfo: React.FC<BackOfficeInfoProps> = ({
  aorId,
  npnId,
  sherpa,
  planTypes,
  isAcaRequest,
  onAorChange,
  onNpnChange,
  onSherpaChange,
}) => {
  const [aors, setAors] = useState<AorData[]>([]);
  const [npns, setNpns] = useState<NpnData[]>([]);

  // Get office ID from user data
  const userData = useSelector((state: RootState) => (state.user as any).userData);
  const officeId = userData?.officeId;

  useEffect(() => {
    const fetchBackOfficeData = async () => {
      if (!officeId) return;
      
      try {
        const data = await BackOfficeService.getBackOfficeData(officeId);
        setAors(data.aors);
        setNpns(data.npns);
      } catch (error) {
        console.error('Error fetching back office data:', error);
      }
    };

    fetchBackOfficeData();
  }, [officeId]);

  // Check if should show AOR (Private Med or Ancillary)
  const showAOR = planTypes?.some((type) => ['Private Med', 'Ancillary'].includes(type)) || false;
  
  // Check if should show NPN (Major Medical)
  const showNPN = planTypes?.some((type) => ['Major Medical'].includes(type)) || false;
  
  // Check if should show Health Sherpa (Major Medical OR ACA request)
  const showSherpa = planTypes?.includes('Major Medical') || isAcaRequest;

  return (
    <>
      <Col sm={24} md={24} lg={24} xl={24}>
        <div className='form-heading'>
          <span>Back Office Information</span>
        </div>
      </Col>

      {showAOR && (
        <Col sm={24} md={24} lg={12} xl={12}>
          <div className='radial-input'>
            <span>AOR:</span>
            <Select
              showSearch
              placeholder='Select an AOR'
              optionFilterProp='children'
              onChange={onAorChange}
              value={aorId || undefined}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}>
              {aors.map((aor) => (
                <Select.Option
                  key={aor._id}
                  value={aor.firstName}
                  label={aor.firstName}>
                  {aor.firstName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>
      )}

      {showNPN && (
        <Col sm={24} md={24} lg={12} xl={12}>
          <div className='radial-input'>
            <span>NPN Override*</span>
            <Select
              showSearch
              placeholder='Select an NPN'
              optionFilterProp='children'
              onChange={onNpnChange}
              value={npnId || undefined}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}>
              {npns.map((npn) => (
                <Select.Option
                  key={npn._id}
                  value={npn.firstName}
                  label={npn.firstName}>
                  {npn.firstName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>
      )}

      {showSherpa && (
        <Col sm={24} md={24} lg={12} xl={12}>
          <div className='radial-input'>
            <label>Health Sherpa *</label>
            <Select
              showSearch
              placeholder='Select Health Sherpa'
              optionFilterProp='children'
              onChange={onSherpaChange}
              value={sherpa || undefined}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}>
              {SHERPA_OPTIONS.map((sherpaOption) => (
                <Select.Option
                  key={sherpaOption}
                  value={sherpaOption}
                  label={sherpaOption}>
                  {sherpaOption}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>
      )}
    </>
  );
};
